import { useState } from 'react';
import type { HeatCell } from '@tm/shared';

/**
 * Тепловая карта таблицы — главный экран прогресса.
 * Шкала расходящаяся: тёплый полюс — «пока не даётся», холодный — «знаю уверенно»,
 * серая середина — «как повезёт». Красный с синим, а не с зелёным: красно-зелёная
 * пара неразличима при самой частой форме дальтонизма.
 */
function color(c: number | null): string {
  if (c === null) return 'var(--heat-empty)';
  if (c < 0.2) return 'var(--heat-low-3)';
  if (c < 0.35) return 'var(--heat-low-2)';
  if (c < 0.5) return 'var(--heat-low-1)';
  if (c < 0.6) return 'var(--heat-mid)';
  if (c < 0.75) return 'var(--heat-high-1)';
  if (c < 0.9) return 'var(--heat-high-2)';
  return 'var(--heat-high-3)';
}

export function Heatmap({ cells }: { cells: HeatCell[] }) {
  const [sel, setSel] = useState<HeatCell | null>(null);
  if (cells.length === 0) return null;

  const values = [...new Set(cells.flatMap((c) => [c.a, c.b]))].sort((x, y) => x - y);
  const n = values.length;
  const W = 320;
  const gutter = 18;
  const size = (W - gutter) / n;

  // Ключ канонический, поэтому клетка (a,b) и (b,a) — одна и та же статистика.
  const byKey = new Map(cells.map((c) => [c.key, c]));
  const at = (a: number, b: number) => byKey.get(a <= b ? `${a}x${b}` : `${b}x${a}`);

  const height = gutter + n * size;

  return (
    <div className="stack" style={{ gap: 8 }}>
      <svg className="heat" viewBox={`0 0 ${W} ${height}`} role="img" aria-label="Карта освоенности таблицы умножения">
        {values.map((v, i) => (
          <text key={`c${v}`} className="lbl" x={gutter + i * size + size / 2} y={12}>{v}</text>
        ))}
        {values.map((v, j) => (
          <text key={`r${v}`} className="lbl" x={gutter / 2} y={gutter + j * size + size / 2 + 3}>{v}</text>
        ))}

        {values.map((a, j) =>
          values.map((b, i) => {
            const cell = at(a, b);
            if (!cell) return null;
            const x = gutter + i * size;
            const y = gutter + j * size;
            const selected = sel?.key === cell.key;
            return (
              <g key={`${a}-${b}`} onClick={() => setSel(selected ? null : cell)}>
                <rect
                  className={selected ? 'cell sel' : 'cell'}
                  x={x}
                  y={y}
                  width={size}
                  height={size}
                  rx={4}
                  fill={color(cell.confidence)}
                />
                {/* Галочка дублирует цвет: смысл не должен держаться на нём одном. */}
                {cell.mastered && (
                  <path
                    className="done"
                    d={`M${x + size * 0.3},${y + size * 0.52} l${size * 0.13},${size * 0.15} l${size * 0.26},-${size * 0.3}`}
                  />
                )}
              </g>
            );
          }),
        )}
      </svg>

      {sel ? (
        <div className="card small" style={{ padding: 10 }}>
          <b>{sel.a} × {sel.b} = {sel.a * sel.b}</b>
          {sel.total === 0 ? (
            <span className="dim"> — ещё не встречался</span>
          ) : (
            <span className="dim">
              {' '}— решён {sel.total - sel.wrong} из {sel.total}, в среднем за{' '}
              {(sel.avgMs / 1000).toFixed(1)} с{sel.mastered ? ', освоен ✓' : ''}
            </span>
          )}
        </div>
      ) : (
        <div className="legend">
          <span className="sw" style={{ background: 'var(--heat-low-3)' }} />
          <span>не даётся</span>
          <span className="sw" style={{ background: 'var(--heat-mid)' }} />
          <span>как повезёт</span>
          <span className="sw" style={{ background: 'var(--heat-high-3)' }} />
          <span>знаю ✓</span>
        </div>
      )}

      <details className="more">
        <summary>Показать таблицей</summary>
        <table className="table-view">
          <thead>
            <tr>
              <th>Пример</th>
              <th className="num">Верно</th>
              <th className="num">Всего</th>
              <th className="num">Время</th>
            </tr>
          </thead>
          <tbody>
            {cells
              .filter((c) => c.total > 0)
              .sort((x, y) => (x.confidence ?? 1) - (y.confidence ?? 1))
              .map((c) => (
                <tr key={c.key}>
                  <td>{c.a} × {c.b}{c.mastered ? ' ✓' : ''}</td>
                  <td className="num">{c.total - c.wrong}</td>
                  <td className="num">{c.total}</td>
                  <td className="num">{(c.avgMs / 1000).toFixed(1)} с</td>
                </tr>
              ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}
